import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styles from '../WagesPage.module.css'
import o from '../../components/Overlay.module.css'
import wo from './overlays.module.css'
import { useLayer } from '../../components/layers'
import { useTasks } from '../../components/TaskCentre'
import { ApiError, apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { fmtStamp } from '../timesheets/time'
import { SCOPE_LABEL, dmy, money, plural, rangeLabel } from './format'
import type { ExportDestination, ExportResult, Preflight, RoutingGroup, Scope } from './types'

// Export payroll (V4 wgExport, §7j.5b). The preflight says what an export
// would contain; the export produces a file from the canonical wage result.
// Two tiers of refusal:
//  - hard block (undetermined allocation, no minimum rate): no figure exists,
//    so Export is disabled with the reason and there is no acknowledge path;
//    the only ways forward are to fix the data or leave those workers out;
//  - issues that still produce a number: "Export anyway" once acknowledged,
//    and the acknowledgement is recorded against the export.
// A file adapter produces a file here. An API push sends the run to the
// connected provider; the server refuses one when this build has no
// connection to it (PROV-1), and a push that fails or gets no answer ends the
// task with the reason (107).
// An adapter a group destination routes (the Customer CSV is "CSV export")
// holds only the workers whose group has it ticked (§7c); which groups are in
// and out is shown beside the issues, not among them, since a group left out
// is the setting working, not something to acknowledge.
// The export itself is a background task (§6d, §10.4): pressing Export closes
// this dialog and the file finishes into the task centre, as does fetching a
// kept file again from "Files produced".
// D6.6 (approved by Kerrin 2026-10-02): an export is always the tenant's pay
// mode, not the view on the Wages table. The server decides it; this dialog
// names it, shows its totals, and sends back what it showed so that a pay
// setting changed in the meantime refuses instead of exporting something else.

interface Props {
  user: any
  from: string
  to: string
  scope: Scope
  // The mode the tenant pays on (the cog); null when not set, read as Daily.
  payMode?: Scope | null
  onClose: () => void
  // F-1: open the existing recalculation for worker/days with no figure.
  onRecalculate?: () => void
  // The export task finished and the run is recorded.
  onExported: () => void
}

// V4 WG_ADAPTERS line copy; readiness is the server's.
const LINES: Record<string, string> = {
  payhero: 'Harvest, Harvest Topup and Paid Break as separate work lines',
  xero: 'One earnings rate per work type, top-up as its own rate',
  agrismart: 'Hours and quantity per team, top-up merged into gross',
  smartly: 'Gross wage per worker per period',
  paysauce: 'Hours, units and allowances per worker',
  csv: 'Every canonical field, one row per wage row in the selected mode',
}

interface LogRow {
  id: string
  period_from: string
  period_to: string
  mode: string
  adapter: string
  acknowledged: boolean
  issue_count: number
  row_count: number
  total_wages: number
  filename: string
  created_at: string
  routed_by?: string | null
  destinations?: ExportDestination[] | null
  // A push's record (107): sent, or unknown (possibly received). A failed
  // push is not an export and is not in this list.
  push_outcome?: 'sent' | 'unknown' | null
  // What produced the file (D1 point 9): the export engine, and the wage-basis
  // versions of the worker/days in it, counted from the snapshot it was built
  // from. An export made before these were recorded has none (null), and says
  // so rather than having a version guessed for it.
  engine_version?: string | null
  basis_versions?: { version: string; worker_days: number }[] | null
}

function provenanceOf(x: Pick<LogRow, 'engine_version' | 'basis_versions'>): string {
  const engine = x.engine_version ? `Engine ${x.engine_version}` : 'Engine not recorded'
  const basis = Array.isArray(x.basis_versions)
    ? x.basis_versions.length
      ? 'basis ' + x.basis_versions.map((b) => `${b.version} (${plural(b.worker_days, 'worker-day', 'worker-days')})`).join(', ')
      : 'no wage basis in the file'
    : 'basis versions not recorded'
  return `${engine} · ${basis}`
}

const modeOf = (x: Pick<LogRow, 'mode'>) => SCOPE_LABEL[x.mode as Scope] || x.mode

const groupName = (g: { group_name: string | null }) => g.group_name || 'No group'

// The groups a logged export put in its file or push, from its own record.
// Exports from before routing recorded nothing, and nothing is inferred for
// them.
function sentTo(x: LogRow) {
  const sent = (x.destinations || []).filter((d) => d.outcome === 'file_produced' || d.outcome === 'pushed' || d.outcome === 'push_unknown')
  return sent.length ? ` · ${sent.map(groupName).join(', ')}` : ''
}

function pushLabel(x: LogRow) {
  if (x.push_outcome === 'sent') return ' · pushed'
  if (x.push_outcome === 'unknown') return ' · pushed, no answer: check the provider'
  return ''
}

export function ExportModal({ user, from, to, scope, payMode, onClose, onRecalculate, onExported }: Props) {
  const [adapter, setAdapter] = useState('csv')
  const [excluded, setExcluded] = useState<string[]>([])
  const [pre, setPre] = useState<Preflight | null>(null)
  const [ack, setAck] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nudge, setNudge] = useState<string | null>(null)
  const [log, setLog] = useState<LogRow[]>([])
  const { startTask } = useTasks()
  const [help, setHelp] = useHelpPref(user, 'wages_export')
  const userRef = useRef(user)
  userRef.current = user
  const seq = useRef(0)
  // A layer (h 425-458). The choices here are how to run one export, not a
  // record being edited, so it closes without asking (the adapter is also
  // switched by the load itself when the saved one is not offered).
  const modalEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: modalEl, modal: true })

  useEffect(() => {
    const n = ++seq.current
    setError(null)
    apiJson<Preflight>(userRef.current, '/api/wages/export/preflight', { body: { from, to, adapter, exclude_human_ids: excluded } })
      .then((p) => {
        if (n !== seq.current) return
        setPre(p)
        // Only the adapters this business offers are listed (h 3494); when the
        // one asked for is not among them, the first one offered is used.
        const offered = p?.adapters || []
        if (offered.length && !offered.some((a) => a.key === adapter)) setAdapter(offered[0].key)
      })
      .catch((e) => n === seq.current && setError(e?.message || 'Could not check the export'))
  }, [from, to, adapter, excluded])

  const loadLog = () =>
    apiJson<{ exports: LogRow[] }>(userRef.current, '/api/wages/exports')
      .then((r) => setLog(Array.isArray(r?.exports) ? r.exports : []))
      .catch(() => setLog([]))
  useEffect(() => {
    loadLog()
  }, [])

  // Everything that must be named before export: the preflight's issues.
  const issues = pre?.issues || []
  const hard = !!pre?.hard_blocked
  const undetermined = (pre?.blockedRows || []).filter((r) => r.issues.some((i) => i.code === 'UNDETERMINED_ALLOCATION'))
  const noMin = pre?.no_min_rate || []
  // F-1: no valid calculated figure for these worker/days. The export is refused
  // while they are in the selection; leaving them out is the person's choice.
  const noBasis = pre?.no_basis || []
  // D6.13: worker/days that cross a clock change and whose payable result cannot
  // be established correctly yet. Refused like a missing figure; no
  // acknowledgement bypasses them, and none is left out or zeroed for the person.
  const dstRows = pre?.dst_unresolved || []
  // h 3496-3497: this business may not export with unresolved issues, so the
  // issues that need an acknowledgement refuse instead, and the workers they
  // name can be left out like a hard block's.
  const notPermitted = pre?.issues_permitted === false && !!pre?.needs_acknowledgement
  const issueWorkers: [string, string][] = notPermitted
    ? issues.filter((i) => i.bad).flatMap((i) => (i.targets || []).map((x) => [x.human_id, x.worker_name || 'Worker'] as [string, string]))
    : []
  const hardWorkers = [...new Map([...undetermined, ...noMin, ...noBasis, ...dstRows].map((r) => [r.human_id, r.worker_name])).entries()]
  const known = new Map([...hardWorkers, ...issueWorkers])
  const leaveOut = [...known.entries()]
  const chosen = pre?.adapters.find((a) => a.key === adapter) || pre?.adapter || null
  const notReady = !!chosen && !chosen.ready
  const empty = !!pre && pre.row_count === 0
  // The mode this export is: the server's word once the check is back, the configured one until then.
  const exportScope: Scope = pre?.scope ?? payMode ?? 'daily'
  const exportAssessment = pre?.assessment ?? (exportScope === 'daily' ? 'per_day' : null)
  const modeSet = pre ? pre.pay_mode_set !== false : !!payMode
  const routing = pre?.routing || null
  const routedIn: RoutingGroup[] = routing ? routing.groups.filter((g) => g.routed) : []
  const routedOut: RoutingGroup[] = routing ? routing.groups.filter((g) => !g.routed) : []

  const layoutProblem = adapter === 'csv' ? pre?.layout_problem?.message || '' : ''
  const blockReason = hard
    ? dstRows.length && !undetermined.length && !noMin.length && !noBasis.length && dstRows.every((r) => r.code === 'ZONE_EVIDENCE_NEEDED')
      ? `Export is not available: ${plural(new Set(dstRows.map((r) => r.human_id)).size, 'worker has', 'workers have')} ${plural(dstRows.length, 'day', 'days')} with a session whose time zone is not recorded or not confirmed, and whose times fall in a clock-change window, so whether it crossed the change cannot be determined. Nothing has been left out or set to zero for them. Confirm the zone on the shift in Timesheets, or leave them out of this export. An acknowledgement does not change this.`
    : dstRows.length && !undetermined.length && !noMin.length && !noBasis.length
      ? `Export is not available: ${plural(new Set(dstRows.map((r) => r.human_id)).size, 'worker has', 'workers have')} ${plural(dstRows.length, 'day', 'days')} that cross a clock change, and the payable result cannot be established correctly yet. Nothing has been left out or set to zero for them. Leave them out of this export, or resolve them. An acknowledgement does not change this.`
      : noBasis.length && !undetermined.length && !noMin.length
      ? `Export is not available: ${plural(new Set(noBasis.map((r) => r.human_id)).size, 'worker has', 'workers have')} no calculated wage figure for ${plural(noBasis.length, 'day', 'days')} in this export. Nothing has been left out or set to zero for them. Recalculate, or leave them out of this export.`
      : `Export is not available: ${plural(hardWorkers.length, 'worker has', 'workers have')} a wage that cannot be calculated. Resolve them, or leave them out of this export.`
    : layoutProblem
      ? layoutProblem
    : chosen?.enabled === false
      ? `${chosen.label} is not one of the payroll adapters this business uses. Turn it on in Wage settings to export with it.`
    : notReady
      ? `${chosen!.label}: ${chosen!.note}`
      : notPermitted
        ? 'This business does not export payroll with unresolved issues. Resolve them, or leave those workers out of this export.'
      : empty && routing && routedOut.length
        ? `Nothing to export: nobody in this period is in a group that sends to ${routing.label}. Tick ${routing.label} on their group in Groups to include them.`
        : empty
          ? 'Nothing to export: no calculated wage rows in this period.'
          : ''
  const hardDisabled = !pre || !!blockReason
  const needsAck = issues.length > 0 && !notPermitted
  const verb = chosen?.kind === 'api' ? 'Push' : 'Export'
  const label = needsAck ? `${verb} anyway` : verb

  const push = chosen?.kind === 'api'
  const unit = push ? 'push' : 'file'

  const go = async () => {
    if (hardDisabled) return
    if (needsAck && !ack) {
      setNudge(`Acknowledge the ${plural(issues.length, 'unresolved issue', 'unresolved issues')} first`)
      return
    }
    // What is sent is fixed now, not when the task runs.
    const body = {
      from,
      to,
      adapter,
      exclude_human_ids: excluded,
      acknowledged: needsAck && ack,
      // What this dialog showed: a pay setting changed since refuses the export.
      expected_mode: pre?.scope,
      expected_assessment: pre?.assessment,
    }
    const note = `${SCOPE_LABEL[exportScope]} · ${rangeLabel(from, to)}`
    if (push) {
      // A push hands back no file. A failed or unanswered push fails the task
      // with the server's reason; an unanswered one is kept as possibly sent,
      // so the page is refreshed for it too.
      startTask({
        label: `Push payroll: ${chosen?.label || adapter}`,
        note,
        run: async () => {
          try {
            const r = await apiJson<ExportResult>(user, '/api/wages/export', { body })
            return { note: `${note} · ${r.message}` }
          } catch (e) {
            // A push that was reserved leaves a record whatever its answer was (unknown, failed, or
            // sent but not recorded): the push record reloads so it shows.
            if (e instanceof ApiError && /^(PUSH_|PROVIDER_EXPORT_)/.test(String(e.body?.code ?? ''))) onExported()
            throw e
          }
        },
        onDone: () => onExported(),
      })
      onClose()
      return
    }
    startTask({
      label: `Export payroll: ${chosen?.label || adapter}`,
      note,
      // The adapter names the file on the server, so the chip carries the
      // task's name until the file exists.
      producesFile: true,
      run: async () => {
        const r = await apiJson<ExportResult>(user, '/api/wages/export', { body })
        return { file: { name: r.filename, content: r.artifact, type: r.content_type }, note: `${note} · ${r.message}` }
      },
      onDone: () => onExported(),
    })
    onClose()
  }

  // A kept file is fetched again as a task too (§6d: never a direct download).
  const redownload = (x: LogRow) =>
    startTask({
      label: 'Payroll file',
      note: `Kept export · ${rangeLabel(x.period_from, x.period_to)} · ${x.adapter}`,
      file: x.filename,
      run: async () => {
        const r = await apiJson<{ filename: string; artifact: string; content_type: string }>(user, `/api/wages/exports/${x.id}`)
        return { file: { name: r.filename, content: r.artifact, type: r.content_type } }
      },
    })

  const toggleExcluded = (id: string) => {
    setAck(false)
    setExcluded((x) => (x.includes(id) ? x.filter((y) => y !== id) : x.concat(id)))
  }

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={modalEl} tabIndex={-1} className={`${o.modal} ${wo.pm} ${wo.pmWide}`} role="dialog" aria-label="Export payroll">
        <div className={`${o.head} ${wo.pmHead}`}>
          <div className={o.headText}>
            <h2 className={`${o.title} ${wo.pmTitle}`}>Export payroll</h2>
          </div>
          {!help && (
            <button
              type="button"
              className={`${wo.miniBtn} ${wo.headBtn}`}
              onClick={() => {
                setHelp(true)
              }}
            >
              How exporting works
            </button>
          )}
        </div>
        <p className={`${o.sub} ${wo.pmSub}`}>
          The engine produces one canonical wage result for your pay mode. Each adapter reshapes that same result for its payroll system; none of them recalculate wages.
        </p>
        <div className={wo.scopeNote} data-testid="export-mode">
          <strong>
            This export is {SCOPE_LABEL[exportScope]} · {rangeLabel(from, to)}
          </strong>
          {modeSet ? ' (your pay mode)' : ' (no pay mode is set, so Daily)'}
          {exportAssessment && (
            <>
              {' '}
              {exportAssessment === 'per_day' ? 'Each day is checked against the minimum on its own, then the top-ups are added.' : 'The whole period is checked against the minimum together, so a strong day can carry a weak one.'}
            </>
          )}
        </div>
        {/* The view never changes what is exported (PAYMODE-1, D6.6). */}
        {exportScope !== scope && (
          <div className={wo.scopeNote} data-testid="paymode-note">
            The Wages table is showing the {SCOPE_LABEL[scope]} view, but this export is always {SCOPE_LABEL[exportScope]}, your pay mode, so its totals can differ from what is on screen. To export another way, change the pay mode in Wage settings first.
          </div>
        )}

        {help && (
          <div className={wo.xHelp}>
            <button
              type="button"
              className={wo.xHelpX}
              aria-label="Hide help"
              onClick={() => {
                setHelp(false)
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
            <div className={wo.xHelpItem}>
              <div className={wo.xHelpLabel}>Which mode is exported</div>
              <p>
                An export always uses the pay mode you set in Wage settings, Daily if none is set. It does not follow the view you are looking at, so looking at another view never changes what is sent. Each export records its mode, shown in the list of files produced.
              </p>
            </div>
            <div className={wo.xHelpItem}>
              <div className={wo.xHelpLabel}>Choosing an adapter</div>
              <p>
                Pick the payroll system you actually pay from. The difference between them is only how the same wages are split into lines: some want a separate top-up line, others want it merged into gross. Your payroll system decides that, not you.
              </p>
            </div>
            <div className={wo.xHelpItem}>
              <div className={wo.xHelpLabel}>API push or file</div>
              <p>
                An API push sends the run straight into a connected account. A file adapter produces a file for you to import by hand. Connections live in{' '}
                <Link to="/integrations" className={styles.linkBtn}>
                  Configuration › Integrations
                </Link>

                . A push is offered only when this build has a connection to that provider and the connection has been tested. Before a push is sent it is recorded here, and the record is under Pushes to providers on the Wages page. If the provider does not answer, CONTROL does not know whether it received the run: the push shows Outcome unknown, merges of the people in it and a second push over the same worker/days are blocked, and someone who can push payroll checks the provider and records what they found. Nothing is assumed and nothing is retried.
              </p>
            </div>
            <div className={wo.xHelpItem}>
              <div className={wo.xHelpLabel}>Which groups are in it</div>
              <p>
                Each group chooses where its data may go, in Groups. The customer CSV holds only the groups that have CSV export ticked, and each of those groups shows the time of its last export. Smartly and PaySauce are not chosen per group yet, so they hold everyone on Pay Workers.
              </p>
            </div>
            <div className={wo.xHelpItem}>
              <div className={wo.xHelpLabel}>The run is kept</div>
              <p>
                Every export records the file produced, the period, who produced it and anything acknowledged as unresolved. Exporting twice does not pay twice, but your payroll system may take the second run as new lines, so check there before repeating one.
              </p>
            </div>
          </div>
        )}

        {!pre && !error && <div className={wo.hint}>Checking the period…</div>}

        {hard && (
          <div className={`${o.danger} ${wo.hardBlock}`} data-testid="export-hard-block">
            <strong>{blockReason}</strong>
            {undetermined.map((r) => (
              <div key={'u' + r.human_id + r.work_date} className={wo.blockLine}>
                {r.worker_name}, {dmy(r.work_date)}: Allocation undetermined — {r.issues.find((i) => i.code === 'UNDETERMINED_ALLOCATION')?.message}
              </div>
            ))}
            {noMin.map((r) => (
              <div key={'m' + r.human_id + r.work_date} className={wo.blockLine}>
                {r.worker_name}, {dmy(r.work_date)}: {r.message}
              </div>
            ))}
            {noBasis.map((r) => (
              <div key={'b' + r.human_id + r.work_date} className={wo.blockLine} data-testid="export-no-basis">
                {r.worker_name}, {dmy(r.work_date)}: no calculated figure ({r.reason === 'failed' ? 'the recalculation failed' : r.reason === 'queued' ? 'queued for calculation' : 'never calculated'})
              </div>
            ))}
            {dstRows.map((r) => (
              <div key={'d' + r.human_id + r.work_date} className={wo.blockLine} data-testid="export-dst-unresolved">
                {r.worker_name}, {dmy(r.work_date)}: {r.message}
              </div>
            ))}
            {noBasis.length > 0 && onRecalculate && (
              <div className={wo.blockLine}>
                <button type="button" className={styles.linkBtn} onClick={onRecalculate}>
                  Queue a recalculation
                </button>
              </div>
            )}
          </div>
        )}

        {leaveOut.length + excluded.length > 0 && (
          <div className={wo.section}>
            <div className={`${o.label} ${wo.pmLabel}`}>Leave out of this export</div>
            <div className={wo.chips}>
              {[...new Map([...leaveOut, ...excluded.map((id) => [id, known.get(id) || pre?.blockedRows.find((b) => b.human_id === id)?.worker_name || 'Worker'] as [string, string])]).entries()].map(([id, name]) => {
                const on = excluded.includes(id)
                return (
                  <button key={id} type="button" aria-pressed={on} className={on ? `${wo.pick} ${wo.pickOn}` : wo.pick} onClick={() => toggleExcluded(id)}>
                    {name}
                  </button>
                )
              })}
            </div>
            <div className={wo.hint}>A worker left out is not in the file at all: nothing is paid for them by this export.</div>
          </div>
        )}

        {!hard && issues.length > 0 && (
          <div className={wo.issueBox}>
            <div className={wo.issueTitle}>Exporting now could pay the wrong money</div>
            <div className={wo.issueLine}>
              {plural(issues.length, 'unresolved issue', 'unresolved issues')} in {rangeLabel(from, to)}.{' '}
              {notPermitted
                ? 'This business does not export past them: resolve them first, or leave those workers out.'
                : 'Resolve them first, or acknowledge them and they are recorded against the export.'}
            </div>
            {issues.map((i) => (
              <div key={i.code} className={wo.issueRow}>
                <span className={wo.issueDot} style={{ background: i.bad ? '#b4522a' : '#c78f2c' }} />
                <span className={wo.issueText}>
                  <strong>{i.title}</strong>
                  <span className={wo.issueDetail}>{i.detail}</span>
                </span>
              </div>
            ))}
            {!notPermitted && (
              <label className={wo.ackRow}>
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => {
                    setAck(e.target.checked)
                    setNudge(null)
                  }}
                />
                <span>I understand these are unresolved and want to export anyway. The acknowledgement is recorded against the export.</span>
              </label>
            )}
          </div>
        )}

        {pre && routing && (
          <div className={wo.section} data-testid="export-routing">
            <div className={`${o.label} ${wo.pmLabel}`}>Groups that send to {routing.label}</div>
            {routedIn.length === 0 && <div className={wo.hint}>None of the groups in this period send to {routing.label}.</div>}
            {routedIn.map((g) => (
              <div key={g.group_id || 'none'} className={wo.blockLine}>
                {groupName(g)}: {g.in_file === g.workers ? plural(g.workers, 'worker', 'workers') : `${g.in_file} of ${plural(g.workers, 'worker', 'workers')}`} in this {unit}
              </div>
            ))}
            {routedOut.length > 0 && (
              <div className={wo.hint}>
                Not in this {unit}: {routedOut.map((g) => `${groupName(g)} (${plural(g.workers, 'worker', 'workers')})`).join(', ')}. Their group does not send to {routing.label}; tick it on the group in Groups to include them.
              </div>
            )}
          </div>
        )}
        {pre && !routing && chosen?.ready && (
          <div className={wo.hint} data-testid="export-routing">
            {chosen.label} is not chosen per group: everyone on Pay Workers for this period is in it.
          </div>
        )}

        <div className={wo.adapters} role="radiogroup" aria-label="Payroll adapter">
          {(pre?.adapters || []).map((a) => (
            <button key={a.key} type="button" role="radio" aria-checked={adapter === a.key} className={adapter === a.key ? `${o.choice} ${o.choiceOn} ${wo.optRow} ${wo.optOn}` : `${o.choice} ${wo.optRow}`} onClick={() => setAdapter(a.key)}>
              <span className={`${o.radio} ${wo.optDot}`} />
              <span className={wo.optText}>
                <span className={wo.optLabel}>
                  {a.label} <span className={wo.optFmt}>{a.kind === 'api' ? 'API push' : 'File'}</span>
                </span>
                <span className={wo.optNote}>{LINES[a.key] || a.note}</span>
                {!a.ready && <span className={wo.optWarn}>{a.note}</span>}
              </span>
            </button>
          ))}
        </div>

        <div className={`${o.warn} ${wo.warnBox}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" /></svg>
          <span>
          These figures come from the hours, production and rates recorded in this system and the wage settings you have entered. Dataphyll does not check them against minimum wage law or any employment agreement, and takes no responsibility for what is paid. Check the export before you pay it, and take advice if you are not certain.
          </span>
        </div>

        {pre && !hard && !empty && (
          <div className={wo.hint}>
            <span data-testid="export-total">
              {SCOPE_LABEL[exportScope]} export: {plural(pre.row_count, 'row', 'rows')} · {money(pre.total_wages)} total wages
            </span>
          </div>
        )}
        {pre?.csv_layout && adapter === 'csv' && (
          // Which columns the file will have (088), set in Wage settings.
          <div className={wo.hint} data-testid="export-csv-layout">
            {pre.csv_layout.custom
              ? `Your column layout: ${plural(pre.csv_layout.columns.length, 'column', 'columns')}, ${pre.csv_layout.columns
                  .slice(0, 4)
                  .map((c) => c.header)
                  .join(', ')}${pre.csv_layout.columns.length > 4 ? ', …' : ''}.`
              : `The standard column layout: every field, ${plural(pre.csv_layout.columns.length, 'column', 'columns')}.`}{' '}
            Change it in Wage settings.
          </div>
        )}
        {blockReason && !hard && <div className={wo.err}>{blockReason}</div>}
        {nudge && <div className={wo.err}>{nudge}</div>}
        {error && <div className={wo.err}>{error}</div>}

        <div className={`${o.foot} ${wo.pmFoot}`}>
          <button
            type="button"
            className={`${o.primary} ${wo.pmBtn}`}
            disabled={hardDisabled}
            aria-disabled={hardDisabled || (needsAck && !ack)}
            data-waiting={needsAck && !ack ? 'ack' : undefined}
            title={blockReason || (needsAck && !ack ? 'Acknowledge the unresolved issues first' : '')}
            onClick={go}
          >
            {label}
          </button>
          <button type="button" className={`${o.ghost} ${wo.pmGhost}`} onClick={requestClose}>
            Cancel
          </button>
        </div>

        {log.length > 0 && (
          <div className={wo.section}>
            <div className={`${o.label} ${wo.pmLabel}`}>{log.some((x) => x.push_outcome) ? 'Files produced and pushes' : 'Files produced'}</div>
            {log.slice(0, 5).map((x) => (
              <div key={x.id} className={wo.logRow}>
                <span>
                  <span>
                    {fmtStamp(x.created_at)} · {rangeLabel(x.period_from, x.period_to)} · {modeOf(x)} · {x.adapter}
                    {pushLabel(x)} · {plural(x.row_count, 'row', 'rows')} · {money(x.total_wages)}
                    {sentTo(x)}
                    {x.acknowledged ? ` · ${plural(x.issue_count, 'issue', 'issues')} acknowledged` : ''}
                  </span>
                  <span className={wo.logProv} data-testid="export-provenance">
                    {provenanceOf(x)}
                  </span>
                </span>
                <button type="button" className={styles.linkBtn} onClick={() => redownload(x)}>
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      {confirm}
    </>
  )
}
